/**
 * ===========================================================================
 * 批量出图的 CSV 表头 —— **唯一真源**
 * ===========================================================================
 *
 * 详情页从「一次一个产品」扩到「一次一批产品」时，多出来的东西只有一样：
 * **产品信息从哪来**。产出形态、平台规格、逐屏解构、提示词函数、报价与提交口径
 * 全部沿用 —— 也正因如此，这一层只该管「表格怎么读」，不该管「表格长什么样」。
 *
 * ⚠️ 2026-09-29 反转：批量**已经是独立工作台**（`/batch`），
 * 不再是详情页里的一个输入形态。上一版这里写着「所以这层不该另开页面」，
 * 那条结论被用户推翻了，原话：
 *   「所以我觉得批量处理这个需要新页面，这样一来，我可以后补图片，
 *     在这个页面继续改不现实」
 * 判据是**寿命**与**输入物**（一个是 `useState` 刷新即失的一次性表单，
 * 一个是能跨天接着补图的作业台账；一个输入「一个产品的四个字段」，
 * 一个输入「一份表格 + 一个文件夹的图」）—— 详见 `pages/BatchStudio.tsx` 的文件头。
 * 这一层本身**一个字都没改**：它从来只负责「CSV → 行」。
 *
 * 三条约束写在这里，而不是散在解析函数与组件里：
 *
 * 1. **列 = 现有表单字段，不发明新字段**。
 *    `productName` / `sellingPoints` / 参考图，就是工作表上方那条参数带里
 *    那几个字段的表格写法。多一列就多一处要跟参数带同步的语义。
 *    ⚠️ 2026-09-30 从五列变三列：`category`（类目）与 `scene`（场景倾向）
 *    随「商品套图」形态一起下架（用户原话「这套图跟详情页重复了」）——
 *    这两项只被套图提示词读过，那条路径已整块删除，留着就是两个
 *    「填了也不会改变任何输出」的列。旧表里若还有这两列，会走下面
 *    「认不出这一列，已忽略」的提示路径，不会报错。
 *    `sku` 是唯一的例外，而且它不是决策 —— 它只是**行的标识**（图片文件名按它
 *    归位、报错信息按它指路），默认留空时用行号顶上。
 *    ⚠️ 2026-10-07：表格里**不再显示**这一列（用户口径「前3列删除」），
 *    但它仍然是这一层的字段 —— 删的是列，不是这个字段。所以凡是「让用户去改
 *    sku」的提示都要重新想一遍：他在界面上改不到（示例行那条提示因此改了判据）。
 *
 * 2. **出图参数列一律拒收**（`model` / `outputs` / `ratio` / `platform` / `tier` …）。
 *    这些是参数带上那一份，全批次共用。表格里再允许一列覆盖，
 *    就是**同一字段的第二个入口** —— 用户改了表格里的比例、却没改参数带上的比例时，
 *    两边都「看起来生效」，实际以哪个为准只能靠猜。这一条是**报错级**，不是提示级：
 *    宁可让用户删掉那一列，也不要静默以某一处为准。
 *
 * 3. **解析只认文本，不执行任何东西**。
 *    纯字符串扫描，不 eval、不 new Function、不拼 HTML。CSV 是从外面拿进来的文件，
 *    它是**数据不是代码**。
 *
 * 图片那一侧**不在这份里**（它不该在这份里）：CSV 是纯文本，图片进不去 ——
 * 用户原话「不过 csv 最大的问题是不带图片，真的搞笑」。所以本层只接受 CSV 里的
 * 公网图地址（`images` 列，仍可用），而「一整个文件夹的图」走
 * `lib/batchImages.ts`（文件名 → 行）+ `components/BatchImageDrop.tsx`（分发器）。
 * 两层刻意分开：一层是**文本进结构化行出**，一层是**文件名进行号出**，都能单测。
 */

/** 一行的产品信息 —— 与页面上方参数带里的同名项**逐字对应** */
export type BatchRow = {
  /** 行标识。留空时由行号派生（`#1`、`#2`…），不参与提示词 */
  sku: string
  productName: string
  sellingPoints: string
  /**
   * 参考图地址。CSV 里多个用 `|` 分隔。
   *
   * ⚠️ 这个字段有**两个来源**：CSV 的 `images` 列，以及本页归位上来的上传结果
   * （`BatchImageDrop` → `upload`）。校验时两者严重度不同 —— 见 `isLocalPath`。
   */
  images: string[]
}

export type IssueLevel = 'error' | 'warn'

export type RowIssue = {
  level: IssueLevel
  field: string
  text: string
}

export type ParsedRow = {
  /** 从 1 开始的**文件行号**（含表头），报错时指得准 */
  line: number
  row: BatchRow
  issues: RowIssue[]
  /** 这一行能不能用 —— 有 error 就不参与提交，warn 只提示 */
  ok: boolean
}

export type ParseResult = {
  rows: ParsedRow[]
  /** 文件级问题：缺必填列 / 混进了参数列 / 空文件 */
  fileIssues: RowIssue[]
  /** 表头认出来了但文件里没数据行 */
  empty: boolean
}

/* ------------------------------ 列清单真源 ------------------------------ */

export type BatchColumn = {
  key: keyof BatchRow
  /** CSV 表头（导出模板用的就是它） */
  header: string
  /** 给用户看的列名 */
  label: string
  required: boolean
  hint: string
}

export const BATCH_COLUMNS: BatchColumn[] = [
  {
    key: 'sku',
    header: 'sku',
    label: 'SKU',
    required: false,
    hint: '行的标识，用于报错指路与图片归位。留空则按行号编号',
  },
  {
    key: 'productName',
    header: 'productName',
    label: '产品名称',
    required: true,
    hint: '必填 —— 没有它这一行后面所有参数都无从谈起',
  },
  {
    key: 'sellingPoints',
    header: 'sellingPoints',
    label: '卖点 / 文案要点',
    required: false,
    hint: '多个卖点用 | 分隔，每个卖点一句话',
  },
  {
    key: 'images',
    header: 'images',
    label: '参考图',
    required: false,
    hint: '公网图片地址，多个用 | 分隔',
  },
]

/** 列名宽容表 —— 用户写中文、写驼峰、写带空格的，都认 */
const HEADER_ALIASES: Record<string, keyof BatchRow> = {
  sku: 'sku',
  编号: 'sku',
  货号: 'sku',
  款号: 'sku',
  productname: 'productName',
  产品名称: 'productName',
  商品名称: 'productName',
  品名: 'productName',
  sellingpoints: 'sellingPoints',
  卖点: 'sellingPoints',
  卖点文案要点: 'sellingPoints',
  文案要点: 'sellingPoints',
  卖点要点: 'sellingPoints',
  images: 'images',
  参考图: 'images',
  图片: 'images',
  图片地址: 'images',
  参考图地址: 'images',
}

/**
 * 出图参数列 —— **拒收**。
 *
 * 见文件头约束 2。这里列出来不是为了「兼容」，是为了**报出人话**：
 * 直接说「这一列属于出图参数，全批次只有页面上方参数带那一份，不接受按行覆盖」，
 * 比默默忽略掉那一列有用得多（用户会以为生效了）。
 */
const FORBIDDEN_HEADERS: Record<string, string> = {
  model: '生成模型',
  outputs: '张数',
  count: '张数',
  ratio: '比例',
  aspectratio: '比例',
  platform: '投放平台',
  tier: '规格档位',
  resolution: '分辨率',
  withmobile: '移动端资产',
  mode: '产出形态',
}

/**
 * 列名归一：去 BOM / 去空白 / 去标点 / 转小写，让上面两张表能逐字对上。
 *
 * ⚠️ `export` 是给**商品资产的 CSV**（`config/productAssets.ts`）复用的。
 * 那边的表头是另一套（标题/卖点/语言/SKU/平台/图片文件名），但「什么叫同一个
 * 列名」这件事必须只有一份判断 —— 两份实现的第一个分歧点一定是「中文括号算不算
 * 分隔符」，而症状是「这一列时认时不认」。
 * 同理导出的还有 `detectDelimiter` 与 `parseDelimited`（见下）。
 */
export function normKey(raw: string): string {
  return raw
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-/\\()（）:：.、]/g, '')
}

/* -------------------------------- 解析 -------------------------------- */

/** 猜分隔符 —— Excel 在中文 Windows 上另存 CSV 会吐分号，北美吐逗号 */
export function detectDelimiter(firstLine: string): string {
  let best = ','
  let bestN = -1
  for (const d of [',', ';', '\t']) {
    const n = firstLine.split(d).length
    if (n > bestN) {
      bestN = n
      best = d
    }
  }
  return best
}

/**
 * 带引号的定界文本扫描。
 *
 * 自己写而不是引依赖：CSV 的坑只有三个（引号内的分隔符、引号内的换行、`""` 转义），
 * 三十行就能盖全；多一个包就多一份要跟着升的供应链风险。
 *
 * ⚠️ `export` 给商品资产的 CSV 复用（同 `normKey` 的说明）：那两个坑
 * （卖点里带换行、文字里带逗号）在商品表里同样会出现，而这是全项目唯一一份
 * 正确实现。抄第二份的结果一定是其中一份先漏掉 `""` 转义。
 */
export function parseDelimited(text: string, delim: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cell += ch
      }
      continue
    }
    if (ch === '"') inQuotes = true
    else if (ch === delim) {
      row.push(cell)
      cell = ''
    } else if (ch === '\n') {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else if (ch !== '\r') {
      cell += ch
    }
  }
  row.push(cell)
  rows.push(row)

  // 全空的行（末尾空行、Excel 的空行）直接丢掉，但**行号在丢之前记住**
  return rows
}

/** 单元格 → 多值：CSV 里用 `|` 分隔卖点与图片 */
function splitMulti(v: string): string[] {
  return v
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
}

const isPublicUrl = (u: string) => /^https?:\/\/\S+$/i.test(u)

/**
 * 明显是**本机磁盘路径** —— 浏览器读不到，也永远传不上去。
 *
 * ===========================================================================
 * 为什么「不是公网链接」必须拆成两级（2026-09-29 实测踩出来的）
 * ===========================================================================
 *
 * `row.images` 有**两个来源**，但严重度完全不同：
 *
 *   ① CSV 导入进来的 —— 用户从 Excel 里粘的，可能真是 `D:/local/ref3.jpg`。
 *      这是**数据错误**，必须拦：他得先上传拿到地址，否则这一行白花钱。
 *
 *   ② 页面自己归位上来的（`BatchImageDrop` → `upload`）—— 这是**系统刚挣来的**
 *      地址。用「上游能不能取到」去预判它，等于「你刚传的图不能用」，
 *      而且**没有任何出路**：mock 模式产出 `blob:`，真后端图床挂时会回落到
 *      `types.ts` 里那个 `localUrl`（本站相对地址 `/static/uploads/…`）。
 *      两者都会被老判据判成 error → 整行的 `ok` 变 false →
 *      刚补完图的行集体掉成「待修」→ 提交按钮少算产品 → **静默错账**。
 *      （CDP 实测过：可用行从 2 掉回 1，按钮从「生成这 2 个产品」变成「1 个」。）
 *
 * 所以判据按「**这是不是一台本机磁盘**」来分，而不是按「是不是公网」：
 * 本地路径 = error（改不了就用不了），其余非 http(s) = warn（能看，但提醒一句）。
 * 这条修法对真实上传通道（图床 CDN 的 https 地址）**零影响**。
 */
const isLocalPath = (u: string) => /^[a-zA-Z]:[\\/]/.test(u) || /^file:/i.test(u) || /^\\\\/.test(u)

/**
 * 解析并校验一份批量 CSV。
 *
 * **不抛异常**：解析失败也是一种结果，要能被渲染成「哪一行哪一列不对」。
 * 抛出去只会让调用点写 try/catch，最后变成一句「导入失败」——
 * 用户拿着 20 行表坐在那儿，不知道改哪。
 */
export function parseBatchCsv(text: string): ParseResult {
  const fileIssues: RowIssue[] = []
  const clean = text.replace(/^\uFEFF/, '')

  if (!clean.trim()) {
    fileIssues.push({ level: 'error', field: '', text: '文件是空的' })
    return { rows: [], fileIssues, empty: true }
  }

  const firstLine = clean.split(/\r?\n/)[0] ?? ''
  const grid = parseDelimited(clean, detectDelimiter(firstLine)).filter((r) =>
    r.some((c) => c.trim() !== ''),
  )

  if (!grid.length) {
    fileIssues.push({ level: 'error', field: '', text: '文件是空的' })
    return { rows: [], fileIssues, empty: true }
  }

  const rawHeader = grid[0]
  const mapped: (keyof BatchRow | null)[] = []
  const seen = new Set<string>()

  rawHeader.forEach((h) => {
    const k = normKey(h)
    if (!k) {
      mapped.push(null)
      return
    }
    const col = HEADER_ALIASES[k]
    if (col) {
      if (seen.has(col)) {
        fileIssues.push({ level: 'warn', field: col, text: `表头里「${h}」出现了不止一次，只取第一列` })
        mapped.push(null)
      } else {
        seen.add(col)
        mapped.push(col)
      }
      return
    }
    const forbidden = FORBIDDEN_HEADERS[k]
    if (forbidden) {
      fileIssues.push({
        level: 'error',
        field: h,
        text: `「${h}」是${forbidden}，属于出图参数 —— 全批次只有顶部「改参数」里那一份，不接受按行覆盖，请删掉这一列`,
      })
      mapped.push(null)
      return
    }
    fileIssues.push({ level: 'warn', field: h, text: `认不出「${h}」这一列，已忽略` })
    mapped.push(null)
  })

  const missing = BATCH_COLUMNS.filter((c) => c.required && !seen.has(c.key))
  missing.forEach((c) => {
    fileIssues.push({ level: 'error', field: c.header, text: `缺必填列「${c.header}」（${c.label}）` })
  })

  const body = grid.slice(1)
  if (!body.length) return { rows: [], fileIssues, empty: true }

  const items = body.map((cells, i) => {
    const get = (key: keyof BatchRow): string => {
      const idx = mapped.indexOf(key)
      return idx >= 0 ? (cells[idx] ?? '').trim() : ''
    }
    const skuRaw = get('sku')
    const row: BatchRow = {
      sku: skuRaw || `#${i + 1}`,
      productName: get('productName'),
      sellingPoints: splitMulti(get('sellingPoints')).join('\n'),
      images: splitMulti(get('images')),
    }
    // 物理行号 = 表头 1 行 + 数据第 i 行；引号内换行会让它对不上，
    // 但报错时给出的是用户在自己的表里最接近的那一行
    return { line: i + 2, row }
  })

  return { rows: validateRows(items), fileIssues, empty: false }
}

/**
 * 逐行校验 —— 解析与**行内编辑后复查**共用同一份规则。
 *
 * 表格里改一个产品名就要重新判一次「这一行还能不能用」，如果复查走另一套判断，
 * 就会出现「导入时说这行不行、改完说行、提交时又拦下来」这种自相矛盾。
 */
export function validateRows(items: { line: number; row: BatchRow }[]): ParsedRow[] {
  const skuSeen = new Map<string, number>()

  return items.map(({ line, row }) => {
    const issues: RowIssue[] = []

    if (!row.productName.trim()) {
      issues.push({ level: 'error', field: 'productName', text: '缺产品名称 —— 这一行出不了图' })
    }
    /*
     * 模板自带的示例行必须能被认出来。
     *
     * 模板里放一行示例是为了让人看懂怎么填，但用户很容易直接连示例一起交上来，
     * 那就是花一份钱出一张「示例产品」的图，而且看不出哪儿错了。
     * 与其把示例写成注释（CSV 没有注释语法），不如给它一个可识别的标记。
     *
     * ⚠️ 2026-10-07 判据从 `sku` 换成**产品名称**，是被删列**逼**出来的一次修正：
     * 表格删掉了「SKU」那一列（用户口径「前3列删除」）之后，`sku` 在界面上
     * **一个入口都没有** —— 它只由 CSV 带进来、或按行号自动编号。
     * 判据还挂在 `sku` 上的话会出现一个死角：用户把产品名换成真的了，
     * 这条提示也会**永远挂着**，而它让用户做的那个动作（「改成真实 SKU」）
     * 他根本做不到 —— 一条永远消不掉的提示，比没有提示更耗人。
     *
     * 所以判据挂在**用户改得动的那个字段**上。模板三行示例的产品名都是
     * 「示例产品 A/B/C：…」，判据取前缀。`smoke-batch-csv.ts` 与
     * `export-batch-template.ts` 里那两条自检只匹配文案里的「示例行」三字，
     * 不受这次判据变更影响。
     */
    if (/^示例产品/.test(row.productName)) {
      issues.push({
        level: 'warn',
        field: 'productName',
        text: '这像是模板里的示例行 —— 确认要出它，就把产品名称换成你自己的',
      })
    }
    if (!row.images.length) {
      issues.push({
        level: 'warn',
        field: 'images',
        text: '没有参考图：产品外观只能靠文字描述，一致性会比有图差很多',
      })
    }
    const bad = row.images.filter(isLocalPath)
    if (bad.length) {
      issues.push({
        level: 'error',
        field: 'images',
        text: `${bad.length} 个图片地址不是公网链接（${bad[0]?.slice(0, 28)}…）—— 本地路径浏览器读不到，请先上传拿到地址`,
      })
    }
    /*
     * 第二级：**不是本机路径，但也不是 http(s)** —— `blob:` / `data:` /
     * 本站相对地址。缩略图在这儿能看到，但上游大概率取不到。
     * 只提示不拦：拦下去用户会卡在一个他没有办法解决的死局里
     * （这些地址本来是这一页自己刚传上去的，见 `isLocalPath` 的注释）。
     */
    const notHttp = row.images.filter((u) => !isLocalPath(u) && !isPublicUrl(u))
    if (notHttp.length) {
      issues.push({
        level: 'warn',
        field: 'images',
        text: `${notHttp.length} 个图片地址不是 http(s) 开头（${notHttp[0]?.slice(0, 28)}…）—— 页面里能看，但出图时上游可能取不到，确认后再提交`,
      })
    }
    if (!row.sellingPoints.trim()) {
      issues.push({ level: 'warn', field: 'sellingPoints', text: '没填卖点，模型会自行发挥' })
    }
    if (row.sku && !/^#\d+$/.test(row.sku)) {
      const prev = skuSeen.get(row.sku)
      if (prev) {
        issues.push({ level: 'error', field: 'sku', text: `SKU「${row.sku}」与第 ${prev} 行重复` })
      } else {
        skuSeen.set(row.sku, line)
      }
    }

    return { line, row, issues, ok: !issues.some((x) => x.level === 'error') }
  })
}

/* ------------------------------ 模板 / 导出 ------------------------------ */

/**
 * 空模板 —— 表头 + **三行**示例。
 *
 * 为什么是三行而不是一行：一行只能演示「全都填满」，而用户真正会问的是
 * 「卖点写多条行不行」「没有参考图行不行」。三行分别演示**三种填法**：
 *   ① 全填（多个卖点 `|` 多个图 `|`）
 *   ② 只有一张参考图
 *   ③ 只填必填（无参考图）
 * 三行的产品名都以「示例产品」开头（`sku` 也带 `SAMPLE-` 前缀，但判据不靠它
 * —— 表格里已经没有 SKU 那一列了，见 `validateRows` 里那段说明），
 * 解析器会把它们全标成「示例行」，所以忘了删也不会静默花钱
 * （见 `validateRows` 与冒烟断言）。
 */
export function batchCsvTemplate(): string {
  const head = BATCH_COLUMNS.map((c) => c.header).join(',')
  // ⚠️ 字段内不能出现半角逗号（会变成列分隔符）。中文顿号/全角冒号都安全。
  const demos = [
    // ① 全填：卖点与参考图都用 `|` 分隔
    ['SAMPLE-001', '示例产品A：硅藻土沥水垫', '0.5s 吸水|可折叠|可机洗', 'https://example.com/ref-1.jpg|https://example.com/ref-2.jpg'],
    // ② 只带一张参考图
    ['SAMPLE-002', '示例产品B：折叠收纳箱', '承重 20kg|免安装', 'https://example.com/ref-3.jpg'],
    // ③ 只填必填：无参考图（解析时会提示「没有参考图」）
    ['SAMPLE-003', '示例产品C：便携榨汁杯', '一键出汁', ''],
  ].map((cells) => cells.join(','))
  return `${head}\n${demos.join('\n')}\n`
}

/** 把行导出成 CSV（改完想留档时用）—— 与模板同表头，可直接再导入 */
export function rowsToCsv(rows: BatchRow[]): string {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
  const head = BATCH_COLUMNS.map((c) => c.header).join(',')
  const body = rows.map((r) =>
    [
      r.sku,
      r.productName,
      r.sellingPoints.split('\n').filter(Boolean).join('|'),
      r.images.join('|'),
    ]
      .map(esc)
      .join(','),
  )
  return [head, ...body].join('\n')
}

/**
 * 逐行提示词导出成一份 TXT。
 *
 * 分节符把「哪一段属于哪一行」标死 —— 20 个产品的提示词拼成一个文件后，
 * 没有分隔就只能靠数数才知道第 37 屏是谁的。
 */
export function rowsToPromptsText(items: { row: BatchRow; prompts: string[] }[]): string {
  return items
    .map(({ row, prompts }) => {
      const head = `===== ${row.sku} · ${row.productName || '（未命名）'} =====`
      const body = prompts.map((p, i) => `----- 第 ${i + 1} / ${prompts.length} 屏 -----\n${p}`)
      return [head, ...body].join('\n\n')
    })
    .join('\n\n\n')
}
